/** ASP.NET model-binding key for JSON that failed to deserialize onto the DTO. */
const MODEL_BINDING_ERROR_KEY = '$';

const GENERIC_VALIDATION_TITLE = /one or more validation errors occurred/i;
const TECHNICAL_ERROR_LINE =
  /json deserialization|missing required properties including|\bfor type\b|path:\s*\$/i;

function isTechnicalApiErrorLine(line: string): boolean {
  return TECHNICAL_ERROR_LINE.test(line);
}

/** Pull user-facing lines from Identity-style `[{ description }]` or ASP.NET `{ Field: ["msg"] }` shapes */
export function collectRegistrationErrorLines(errors: unknown): string[] {
  const lines: string[] = [];

  if (Array.isArray(errors)) {
    for (const item of errors) {
      if (typeof item === 'string') {
        if (item.trim()) {
          lines.push(item.trim());
        }
        continue;
      }
      if (item && typeof item === 'object') {
        const o = item as Record<string, unknown>;
        const desc = o.description ?? o.Description;
        if (typeof desc === 'string' && desc.trim()) {
          lines.push(desc.trim());
          continue;
        }
        const msg = o.message ?? o.Message;
        if (typeof msg === 'string' && msg.trim()) {
          lines.push(msg.trim());
        }
      }
    }
    return Array.from(new Set(lines.filter((line) => !isTechnicalApiErrorLine(line))));
  }

  if (errors && typeof errors === 'object') {
    for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
      if (key === MODEL_BINDING_ERROR_KEY) {
        continue;
      }
      if (Array.isArray(value)) {
        for (const msg of value) {
          if (typeof msg === 'string' && msg.trim()) {
            lines.push(msg.trim());
          }
        }
      } else if (typeof value === 'string' && value.trim()) {
        lines.push(value.trim());
      }
    }
    return Array.from(new Set(lines.filter((line) => !isTechnicalApiErrorLine(line))));
  }

  return [];
}

export function formatRegistrationError(data: Record<string, unknown>): string {
  const topMessage =
    typeof data.message === 'string' && data.message.trim() ? data.message.trim() : '';

  const detailLines = collectRegistrationErrorLines(data.errors);

  if (detailLines.length > 0) {
    return topMessage && !isTechnicalApiErrorLine(topMessage)
      ? `${topMessage}\n${detailLines.join('\n')}`
      : detailLines.join('\n');
  }

  if (typeof data.detail === 'string' && data.detail.trim()) {
    return data.detail.trim();
  }

  if (topMessage) {
    return topMessage;
  }

  if (typeof data.title === 'string' && data.title.trim() && !GENERIC_VALIDATION_TITLE.test(data.title)) {
    return data.title.trim();
  }

  return 'Registration failed';
}
